import React, { useState } from 'react'
import { Button } from "../components/ui/button"
import { Input } from "../components/ui/input"
import { ScrollArea } from "../components/ui/scroll-area"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../components/ui/card"
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar"
import { Badge } from "../components/ui/badge"
import { Clock, Heart, MessageCircle, Bookmark, TrendingUp } from 'lucide-react'


const categories = [
  "All", "Childhood", "Career", "Relationships", "Travel", "Life Lessons", "Historical Events"
]


const categoryColors = {
  "Career": "bg-blue-100",
  "Relationships": "bg-pink-100",
  "Childhood": "bg-yellow-100",
  "Life Lessons": "bg-green-100",
  "Historical Events": "bg-purple-100",
  "Travel": "bg-orange-100"
};




const stories = [
  {
    id: 1,
    title: "The Day I Discovered My Passion for Coding",
    excerpt: "It was a rainy afternoon when I first stumbled upon an old computer in my grandfather's attic...",
    author: "Alex Johnson",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Career",
    readTime: 5,
    likes: 128,
    comments: 32,
    trending: true
  },
  {
    id: 2,
    title: "Love in the Time of Social Distancing",
    excerpt: "Who would have thought that a global pandemic would lead me to the love of my life?",
    author: "Samantha Lee",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Relationships",
    readTime: 7,
    likes: 256,
    comments: 64,
    trending: true
  },
  {
    id: 3,
    title: "From Playground to Olympic Stadium",
    excerpt: "My journey from a small-town kid to representing my country at the Olympics was filled with...",
    author: "Michael Phelps",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Childhood",
    readTime: 10,
    likes: 512,
    comments: 128,
    trending: false
  },
  {
    id: 4,
    title: "The Family Recipe That Survived Three Generations",
    excerpt: "This isn't just about a cake recipe; it's about preserving our family's legacy through flavors...",
    author: "Maria Garcia",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Life Lessons",
    readTime: 6,
    likes: 96,
    comments: 24,
    trending: false
  },
  {
    id: 5,
    title: "Witnessing History: My Experience at the Berlin Wall",
    excerpt: "I was there when the wall fell. The emotions, the unity, the hope - it was all palpable...",
    author: "Hans Schmidt",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Historical Events",
    readTime: 8,
    likes: 384,
    comments: 96,
    trending: true
  },
  {
    id: 6,
    title: "How a Spontaneous Road Trip Changed My Life",
    excerpt: "With nothing but a backpack and a map, I set out on a journey that would redefine my entire existence...",
    author: "Emma Wilson",
    avatar: "/placeholder.svg?height=40&width=40",
    category: "Travel",
    readTime: 9,
    likes: 192,
    comments: 48,
    trending: false,
  }
];


export default function StoriesPage() {
  const [selectedCategory, setSelectedCategory] = useState("All")
  const [searchTerm, setSearchTerm] = useState("")


  const filteredStories = stories.filter(story =>
    (selectedCategory === "All" || story.category === selectedCategory) &&
    (story.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
     story.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))
  )


  return (
    <div className="p-6 bg-gray-50">
      <h1 className="text-3xl font-bold mb-6">Discover Life Stories</h1>
     
      <div className="mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
        <Input
          type="text"
          placeholder="Search stories..."
          className="max-w-xs"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <Button
              key={category}
              variant={selectedCategory === category ? "default" : "outline"}
              onClick={() => setSelectedCategory(category)}
              className="rounded-full"
            >
              {category}
            </Button>
          ))}
        </div>
      </div>


      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredStories.map((story) => (
          <Card
            key={story.id}
            className={`overflow-hidden transition-all duration-300 ease-in-out hover:shadow-lg hover:scale-105 ${categoryColors[story.category] || 'bg-gray-100'}`}
          >
            <CardHeader className="relative">
              <div className="flex justify-between items-start">
                <Badge variant="secondary" className="bg-white text-gray-700">{story.category}</Badge>
                {story.trending && (
                  <Badge variant="secondary" className="bg-yellow-300 text-yellow-800">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Trending
                  </Badge>
                )}
              </div>
              <CardTitle className="mt-2 text-xl font-bold">{story.title}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 line-clamp-3">{story.excerpt}</p>
            </CardContent>
            <CardFooter className="flex flex-col space-y-4 bg-white bg-opacity-60">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center space-x-2">
                  <Avatar>
                    <AvatarImage src={story.avatar} alt={story.author} />
                    <AvatarFallback>{story.author.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{story.author}</span>
                </div>
                <div className="flex items-center text-gray-500 text-sm">
                  <Clock className="w-4 h-4 mr-1" />
                  {story.readTime} min read
                </div>
              </div>
              <div className="flex justify-between items-center w-full text-gray-500">
                <Button variant="ghost" size="sm" className="hover:text-red-500">
                  <Heart className="w-4 h-4 mr-1" />
                  {story.likes}
                </Button>
                <Button variant="ghost" size="sm" className="hover:text-blue-500">
                  <MessageCircle className="w-4 h-4 mr-1" />
                  {story.comments}
                </Button>
                <Button variant="ghost" size="sm" className="hover:text-green-500">
                  <Bookmark className="h-4 w-4 mr-1" />
                  Save
                </Button>
              </div>
            </CardFooter>
          </Card>
        ))}
      </div>
    </div>
  )
}
